import { useMutation, useQuery } from "@apollo/client";
import * as palette from "@govlaunch/palette";
import uniqBy from "lodash/fp/uniqBy";
import {
  StoryActions,
  StoryAuthor,
  StoryAwards,
  StoryCoauthors,
  StoryCollaborators,
  StoryGallery,
  StoryHeader,
  StoryNewsworthy,
  StoryProducts,
  StoryRelatedStoriesSmall,
  StoryHighlights,
  StoryResources,
  StoryGovernments,
} from "@govlaunch/story";
import { IStoryFieldsFragment } from "@govlaunch/story/src/fragments/__generated__/StoryFieldsFragment.generated";
import {
  Anchor,
  Box,
  Flex,
  Heading,
  LoadingSpinner,
  PageGridContainer,
  SimpleGrid,
  Stack,
  Tag,
  TagText,
} from "@govlaunch/web";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { ReactNode, useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelfie } from "~/components/auth/Auth";
import StoriesLinkIcon from "~/components/icons/StoriesLinkIcon";
import { findLanguageByLocale } from "~/intl/locales";
import useIsMobile from "~/lib/hooks/useIsMobile";
import ToggleBookmarkMutation from "~/lib/mutations/ToggleBookmarkMutation";
import ToggleUpvoteMutation from "~/lib/mutations/ToggleUpvoteMutation";
import {
  IToggleBookmarkMutation,
  IToggleBookmarkMutationVariables,
} from "~/lib/mutations/__generated__/ToggleBookmarkMutation.generated";
import {
  IToggleUpvoteMutation,
  IToggleUpvoteMutationVariables,
} from "~/lib/mutations/__generated__/ToggleUpvoteMutation.generated";
import { useIntlLocale } from "~/lib/withIntl";
import { IBookmarkType, ICity, IGovernment, IUpvoteTargetType, Maybe } from "~/types/types";
import nextLinkHref from "~/src/utils/nextLinkHref";
import stringOrFirst from "~/src/utils/stringOrFirst";
import useSignInModal from "~/src/components/auth/useSignInModal";
import TranslateTo from "~/src/components/translation/TranslateTo";
import StoryBySlugQuery from "~/src/components/story/graphql/StoryBySlugQuery";
import {
  IStoryBySlugQuery,
  IStoryBySlugQueryVariables,
} from "~/src/components/story/graphql/__generated__/StoryBySlugQuery.generated";
import hidePodcastTag from "~/src/components/story/pages/hidePodcastTag";
import notUndefinedOrNull from "~/lib/utils/notUndefinedOrNull";
import { constructCoauthors } from "@govlaunch/story/src/components/StoryAuthor";
import { NoStory } from "~/src/components/collection/placeholders";
import BackToTop from "~/components/qol/BackToTop";
import StoryPageSeo from "~/components/seo/StoryPageSeo";
import StoryPageSummary from "./StoryPageSummary";

export default function StoryPage() {
  const router = useRouter();
  const viewer = useSelfie();
  const { open: openSignInModal } = useSignInModal();
  const isMobile = useIsMobile();
  const lang = stringOrFirst(router.query.lang);
  const langExists = typeof lang !== "undefined" && lang.length > 0 && lang !== "en-US";
  const { locale } = useIntlLocale();
  const targetLanguage = lang || locale;
  const showTranslateButton = langExists || locale !== "en-US";
  const storySlug = stringOrFirst(router.query.storySlug);

  const { data, loading, refetch } = useQuery<IStoryBySlugQuery, IStoryBySlugQueryVariables>(StoryBySlugQuery, {
    variables: {
      slug: storySlug,
      language: findLanguageByLocale(lang),
    },
    fetchPolicy: "network-only",
  });

  const [toggleUpvote] = useMutation<IToggleUpvoteMutation, IToggleUpvoteMutationVariables>(ToggleUpvoteMutation);
  const [toggleBookmark] = useMutation<IToggleBookmarkMutation, IToggleBookmarkMutationVariables>(
    ToggleBookmarkMutation,
  );

  const [mainTitleHeight, setMainTitleHeight] = useState(0);
  const headerRef = useCallback((node) => {
    if (node !== null) {
      // eslint-disable-next-line
      function handleResize() {
        setMainTitleHeight(node.getBoundingClientRect().height);
      }

      handleResize();

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const [mobileReadMoreSummaryOpened, setMobileReadMoreSummaryOpened] = useState(false);

  const editorContainerRef = useCallback((node) => {
    if (node !== null) {
      if (node.getBoundingClientRect().height < 3000) {
        setMobileReadMoreSummaryOpened(true);
      }
    }
  }, []);

  if (loading) {
    return (
      <Flex justify="center">
        <LoadingSpinner />
      </Flex>
    );
  }

  if (!data?.storyBySlug) {
    return <NoStory storySlug={storySlug} showSignInMessage={!viewer} />;
  }

  const { storyBySlug: story } = data;

  const locations = constructLocations(story);

  const coauthors = constructCoauthors(story.coauthors, story.vendorCoauthors);

  return (
    <div>
      <StoryPageSeo story={story} />

      <PageGridContainer>
        <Flex direction="row" justifyContent="space-between" alignItems="center">
          <SimpleGrid gridTemplateColumns="40px 1fr" alignItems="center" gridColumnGap={6} mb={4}>
            <StoriesLinkIcon />

            <div>
              <Link href="/stories" as="/stories" passHref={true} legacyBehavior={true}>
                <Anchor color="black" textTransform="uppercase" fontSize="md">
                  <FormattedMessage defaultMessage="Stories" id="Ldxym4" />
                </Anchor>
              </Link>
            </div>
          </SimpleGrid>

          <TranslateTo
            langExists={langExists}
            showTranslateButton={showTranslateButton}
            refetch={() =>
              refetch({
                slug: storySlug,
                language: findLanguageByLocale(targetLanguage),
              })
            }
            refetchOriginal={() =>
              refetch({
                slug: storySlug,
                language: null,
              })
            }
          />
        </Flex>
      </PageGridContainer>

      {isMobile && (
        <Box css={{ marginBottom: 20 }}>
          <PageGridContainer>
            <StoryActions
              story={story}
              iconsArrangement="space-around"
              onToggleUpvote={() => {
                if (!viewer) {
                  openSignInModal();

                  return;
                }

                toggleUpvote({
                  variables: {
                    targetType: IUpvoteTargetType.Story,
                    targetId: story._id,
                  },
                });
              }}
              onToggleBookmark={() => {
                if (!viewer) {
                  openSignInModal();

                  return;
                }

                toggleBookmark({
                  variables: {
                    targetType: IBookmarkType.Story,
                    targetId: story._id,
                  },
                });
              }}
            />
          </PageGridContainer>
        </Box>
      )}

      <article>
        <PageGridContainer gridTemplateColumns={["1fr", "1fr", "3fr 1fr"]} mt={3}>
          <div id="main-content">
            <Stack shouldWrapChildren={true} spacing={6}>
              {/* Header */}
              <StoryHeader story={hidePodcastTag(story)} headerRef={headerRef} />

              {/* Author */}
              <StoryAuthor
                story={story}
                isSmall={true}
                renderLinkWrap={(story, children) => {
                  if (!story.author) {
                    return <span />;
                  }

                  if (story.author.isSiteAdmin || story.author.isLimitedSiteAdmin) {
                    return (
                      <Link href="/[userSlug]/[[...tab]]" as="/@lindsay-pica" passHref={true} legacyBehavior={true}>
                        <a>{children}</a>
                      </Link>
                    );
                  }

                  return (
                    <Link
                      href={`/user?userSlug=${story.author.slug}`}
                      as={`/@${story.author.slug}`}
                      passHref={true}
                      legacyBehavior={true}
                    >
                      <a>{children}</a>
                    </Link>
                  );
                }}
              />
              <div css={{ maxWidth: isMobile ? "100%" : "90%" }}>
                {/* Gallery */}
                {story.medias && story.medias.length > 0 && <StoryGallery story={story} />}

                {/* Newsworthy */}
                {story.newsworthy && story.newsworthy.length > 0 && (
                  <div
                    css={{
                      borderBottom: "1px solid",
                      borderColor: palette.lightGray,
                      paddingBottom: 20,
                      marginBottom: 25,
                    }}
                  >
                    <StoryNewsworthy story={story} stacked={isMobile} />
                  </div>
                )}

                {/* Highlights */}
                {story.highlights && story.highlights.length > 0 && (
                  <StoryHighlights highlights={story.highlights.filter((highlight) => highlight)} />
                )}

                {/* Body */}
                <div css={{ marginBottom: 30 }}>
                  <Heading
                    as="h2"
                    css={{
                      position: "absolute",
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: "hidden",
                      clip: "rect(0,0,0,0)",
                      border: 0,
                      color: palette.white,
                    }}
                  >
                    <FormattedMessage defaultMessage="Summary" id="RrCui3" />
                  </Heading>

                  <div
                    id="summaryWrapper"
                    css={
                      isMobile && !mobileReadMoreSummaryOpened
                        ? { height: 2200, overflow: "hidden", position: "relative" }
                        : {}
                    }
                  >
                    <div ref={editorContainerRef}>
                      <StoryPageSummary body={story.body} />
                    </div>
                    {isMobile && !mobileReadMoreSummaryOpened && (
                      <div
                        css={{
                          width: "100%",
                          height: 150,
                          position: "absolute",
                          bottom: 0,
                          backgroundImage: "linear-gradient(to bottom, rgb(255 255 255 / 0%), rgb(250 250 250))",
                          zIndex: 100,
                        }}
                      />
                    )}
                  </div>
                  {isMobile && !mobileReadMoreSummaryOpened && (
                    <button
                      type="button"
                      css={{
                        type: "button",
                        width: "90%",
                        height: 45,
                        fontWeight: 400,
                        fontSize: 20,
                        border: "2px solid",
                        borderColor: palette.lightGray,
                        borderRadius: 5,
                        margin: 20,
                        color: palette.darkGray,
                      }}
                      onClick={() => {
                        setMobileReadMoreSummaryOpened(true);
                      }}
                    >
                      <FormattedMessage defaultMessage="Read More" id="4Cltcc" />
                    </button>
                  )}
                </div>
              </div>
            </Stack>
          </div>

          {/* Side bar */}
          <div css={{ position: "relative", paddingTop: isMobile ? 0 : mainTitleHeight, minWidth: 360 }}>
            <Box css={{ position: "sticky", top: 0 }}>
              {!isMobile && (
                <StoryActions
                  story={story}
                  iconsArrangement="flex-start"
                  onToggleUpvote={() => {
                    if (!viewer) {
                      openSignInModal();

                      return;
                    }

                    toggleUpvote({
                      variables: {
                        targetType: IUpvoteTargetType.Story,
                        targetId: story._id,
                      },
                    });
                  }}
                  onToggleBookmark={() => {
                    if (!viewer) {
                      openSignInModal();

                      return;
                    }

                    toggleBookmark({
                      variables: {
                        targetType: IBookmarkType.Story,
                        targetId: story._id,
                      },
                    });
                  }}
                />
              )}

              <Heading
                as="h2"
                css={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  border: 0,
                  color: palette.white,
                }}
              >
                <FormattedMessage defaultMessage="Additional Story Information" id="yJE2qC" />
              </Heading>

              {/* Tags */}
              {story.tags.length > 0 && (
                <div css={{ marginTop: "3rem", display: "flex", flexWrap: "wrap", flexDirection: "row" }}>
                  {story.tags.map((tag) => {
                    const linkHref = nextLinkHref("/collections/[collectionSlug]/[[...tab]]", {
                      collectionSlug: tag.slug,
                    });

                    const linkAs = `/collections/${tag.slug}/stories`;

                    return (
                      <Link key={linkAs} href={linkHref} as={linkAs} legacyBehavior={true}>
                        <a href={linkAs}>
                          <Tag mr={2} mb={2} key={tag._id} color="black" title={tag.name}>
                            <TagText>{tag.name}</TagText>
                          </Tag>
                        </a>
                      </Link>
                    );
                  })}
                </div>
              )}

              <div css={{ borderLeft: isMobile ? "none" : "1px solid", borderColor: palette.lightGray, marginTop: 50 }}>
                {/* Coauthors */}
                {coauthors.length > 0 && (
                  <Box mt={4} pl={3} pt={5}>
                    <StoryCoauthors
                      coauthors={coauthors}
                      isMobile={false}
                      renderLinkWrap={(author, children) => {
                        if (!author) {
                          return <span />;
                        }

                        if (author.isSiteAdmin || author.isLimitedSiteAdmin) {
                          return (
                            <Box width="100%" key={author._id}>
                              <Link
                                href="/[userSlug]/[[...tab]]"
                                as="/@lindsay-pica"
                                passHref={true}
                                legacyBehavior={true}
                              >
                                <a>{children}</a>
                              </Link>
                            </Box>
                          );
                        }

                        return (
                          <Box width="100%" key={author._id}>
                            <Link
                              href={`/user?userSlug=${author.slug}`}
                              as={`/@${author.slug}`}
                              passHref={true}
                              legacyBehavior={true}
                            >
                              <a>{children}</a>
                            </Link>
                          </Box>
                        );
                      }}
                      renderLinkWrapVendor={(vendor, children) => {
                        if (!vendor) {
                          return children;
                        }

                        return (
                          <Box width="100%" key={vendor._id}>
                            <Link
                              href={`/vendors/${vendor.slug}`}
                              as={`/vendors/${vendor.slug}`}
                              passHref={true}
                              legacyBehavior={true}
                            >
                              <a>{children}</a>
                            </Link>
                          </Box>
                        );
                      }}
                    />
                  </Box>
                )}

                {/* Collaborators */}
                {story.collaborators.length > 0 && (
                  <Box mt={8} pl={3}>
                    <StoryCollaborators
                      collaborators={story.collaborators}
                      renderLinkWrap={(user, children) => {
                        if (!user.slug) {
                          return children;
                        }

                        return (
                          <Link
                            href={`/user?userSlug=${user.slug}`}
                            as={`/@${user.slug}`}
                            passHref={true}
                            legacyBehavior={true}
                          >
                            <a>{children}</a>
                          </Link>
                        );
                      }}
                    />
                  </Box>
                )}

                {/* Governments */}
                {locations.length > 0 && (
                  <Box mt={8} pl={3}>
                    <StoryGovernments
                      locations={locations}
                      renderLinkWrap={(location, children) => {
                        if (!location) {
                          return children;
                        }

                        return (
                          <Link
                            href={`/government?governmentSlug=${location.slug}`}
                            as={`/governments/${location.slug}`}
                            key={location._id}
                            legacyBehavior={true}
                          >
                            <a>{children}</a>
                          </Link>
                        );
                      }}
                    />
                  </Box>
                )}

                {/* Products */}
                {story.products && story.products.length > 0 && (
                  <Box mt={8} pl={3}>
                    <StoryProducts
                      products={story.products}
                      renderLinkWrap={(product, children) => {
                        return (
                          <Link
                            href={`/products/${product.slug}`}
                            passHref={true}
                            key={product._id}
                            legacyBehavior={true}
                          >
                            <a>{children}</a>
                          </Link>
                        );
                      }}
                    />
                  </Box>
                )}

                {/* Resources */}
                {story.resources &&
                  story.resources.filter(({ __typename }) => __typename !== "TextResource").length > 0 && (
                    <Box mt={8} pl={3}>
                      <StoryResources resources={story.resources} />
                    </Box>
                  )}

                {/* Awards */}
                {story.awards && story.awards.length > 0 && (
                  <Box mt={8} pl={3}>
                    <StoryAwards mt={4} story={story} />
                  </Box>
                )}

                <Box height="5px" bg="blue.600" mx={3} my={5} />

                {/* Related Stories */}
                <Box mt={8} pl={3}>
                  <StoryRelatedStoriesSmall
                    relatedStories={(story.relatedStories || []).map((story) => {
                      return {
                        ...story,
                        tags: [],
                      };
                    })}
                    mt={4}
                    renderLinkWrap={StoryLinkWrap}
                    placeholderImageSrc="/static/story/story-card-placeholder.png"
                    renderTagLinkWrap={(tag, children) => {
                      const linkHref = nextLinkHref("/collections/[collectionSlug]/[[...tab]]", {
                        collectionSlug: tag.slug,
                      });
                      const linkAs = `/collections/${tag.slug}/stories`;
                      return (
                        <Link key={linkAs} href={linkHref} as={linkAs} legacyBehavior={true}>
                          <a href={linkAs}>{children}</a>
                        </Link>
                      );
                    }}
                    renderAuthorLinkWrap={() => {
                      return <span />;
                    }}
                  />
                </Box>
              </div>
            </Box>
          </div>
        </PageGridContainer>
      </article>
      <BackToTop />
    </div>
  );
}

function StoryLinkWrap(story: NonNullable<IStoryFieldsFragment["relatedStories"]>[0], children: ReactNode) {
  return (
    <Link
      href="/stories/[storySlug]"
      as={`/stories/${story.slug}`}
      passHref={true}
      key={story._id}
      legacyBehavior={true}
    >
      {children}
    </Link>
  );
}

type LocationType = Maybe<
  {
    __typename: "Government";
  } & Pick<IGovernment, "_id" | "slug" | "name" | "logo"> & {
      city: {
        __typename: "City";
      } & Pick<ICity, "_id" | "country">;
    }
>;

function constructLocations(story: NonNullable<IStoryFieldsFragment>) {
  const locations = (story.locations || new Array<LocationType>()).filter(notUndefinedOrNull);
  if (story.government) {
    locations.unshift(story.government);
  }

  return uniqBy((location) => (location as any)._id, locations);
}
